import React, { useState, useEffect, Dispatch, SetStateAction } from 'react';

import {
  PartnerList,
  ValidationFeedback,
  FormSubmitionValues,
  FormStatus,
  DefaultComponentProps,
  PartnerInfo,
  Platforms,
  CreateUserResultTypes,
} from '../../types';
import { isEmpty, isOnlyNumbers, isSpecificLength, isValidEmail } from '../../validation';
import { ariaInvalid } from '../../helpers';

import { CardBody, CardHeader, PartnerSelect, ValidationSummary } from '..';

import './style.scss';

interface Props extends DefaultComponentProps {
  partnerList: PartnerList;
  formSubmitionValues: FormSubmitionValues;
  setFormSubmitionValues: Dispatch<SetStateAction<FormSubmitionValues>>;
  setPartnerInfo: Dispatch<SetStateAction<PartnerInfo>>;
  partnerInfo: PartnerInfo;
  submitPendingUser: (formValues: FormSubmitionValues) => void;
  referredPartnerNumber: string;
  setView: (resultType: CreateUserResultTypes) => void;
  setTryAgainView: Dispatch<SetStateAction<CreateUserResultTypes>>;
}

export const UserSubmit = ({
  partnerList,
  formSubmitionValues,
  setFormSubmitionValues,
  partnerInfo,
  setPartnerInfo,
  textConfig,
  submitPendingUser,
  referredPartnerNumber,
  setView,
  setTryAgainView,
}: Props) => {
  const {
    title,
    ingress,
    formfieldPhoneLabel,
    formfieldEmailLabel,
    formfieldCustomerNumberLabel,
    formfieldPinLabel,
    formSubmitButtonText,
    legacyHelp,
    legacyPinLink,
  } = textConfig.userSubmit;

  const { formValidationFeedback } = textConfig.validationSummary;
  const [validationFeedback, setValidationFeedback] = useState({
    partner: formValidationFeedback.partnerNumberInvalid,
    phone: formValidationFeedback.mobileNumberInvalid,
    email: formValidationFeedback.emailInvalid,
    customerNumber: '',
    pin: '',
  } as ValidationFeedback);
  const [formStatus, setFormStatus] = useState({
    valid: false,
    status: FormStatus.INITIAL,
  });

  useEffect(() => {
    if (referredPartnerNumber) {
      setFormSubmitionValues((state) => ({ ...state, partnerNumber: referredPartnerNumber }));
      setValidationFeedback((state) => ({ ...state, partner: '' }));
    }

    setFormStatus((state) => ({ ...state, valid: Object.values(validationFeedback).every((feedback) => feedback === '') }));
  }, [referredPartnerNumber, validationFeedback, setFormSubmitionValues, formSubmitionValues]);

  const handlePartnerChanged = ({ currentTarget }: React.SyntheticEvent<HTMLSelectElement>) => {
    const { value } = currentTarget;
    if (isEmpty(value)) {
      setValidationFeedback((state) => ({ ...state, partner: formValidationFeedback.partnerNumberInvalid }));
      return;
    }

    const selectedPartner = partnerList.find((partner) => String(partner.partnerId) === value);
    if (!selectedPartner) {
      setValidationFeedback((state) => ({ ...state, partner: formValidationFeedback.partnerNumberInvalid }));
      return;
    }

    // reset validation errors for not selected platform
    if (selectedPartner.platform === Platforms.LEGACY) {
      setValidationFeedback((state) => ({ ...state, phone: '', email: '' }));
    } else if (selectedPartner.platform === Platforms.BLOCKED) {
      setView(CreateUserResultTypes.CRM_BLOCKED);
    } else {
      setValidationFeedback((state) => ({ ...state, customerNumber: '', pin: '' }));
    }

    const crmSystemChanged = partnerInfo.platform !== selectedPartner.platform;
    if (crmSystemChanged) {
      setFormSubmitionValues((state) => ({
        ...state,
        mobileNumber: '',
        email: '',
        customerNumber: '',
        pin: '',
        partnerNumber: value,
      }));
      setValidationFeedback((state) => ({
        ...state,
        phone: selectedPartner.platform === Platforms.LEGACY ? '' : formValidationFeedback.mobileNumberInvalid,
        email: selectedPartner.platform === Platforms.LEGACY ? '' : formValidationFeedback.emailInvalid,
        partner: '',
        customerNumber: selectedPartner.platform === Platforms.LEGACY ? formValidationFeedback.customerNumberInvalid : '',
        pin: selectedPartner.platform === Platforms.LEGACY ? formValidationFeedback.pinInvalid : '',
      }));
      setFormStatus((state) => ({ ...state, status: FormStatus.INITIAL }));
    } else {
      setFormSubmitionValues((state) => ({ ...state, partnerNumber: value }));
      setValidationFeedback((state) => ({ ...state, partner: '' }));
    }

    setPartnerInfo({
      partnerNumber: String(selectedPartner.partnerId),
      hostname: selectedPartner.hostname,
      platform: selectedPartner.platform,
      name: selectedPartner.name,
    });
  };

  const handleMobileNumber = ({ currentTarget }: React.SyntheticEvent<HTMLInputElement>) => {
    const { value } = currentTarget;
    setFormSubmitionValues((state) => ({ ...state, mobileNumber: value }));
    if (!isEmpty(value) && isOnlyNumbers(value) && isSpecificLength(value, 8)) {
      setValidationFeedback((state) => ({ ...state, phone: '' }));
    } else {
      setValidationFeedback((state) => ({ ...state, phone: formValidationFeedback.mobileNumberInvalid }));
    }
  };

  const handleEmail = ({ currentTarget }: React.SyntheticEvent<HTMLInputElement>) => {
    const { value } = currentTarget;
    setFormSubmitionValues((state) => ({ ...state, email: value }));
    setValidationFeedback((state) => ({ ...state, email: isValidEmail(value) ? '' : formValidationFeedback.emailInvalid }));
  };

  const handleCustomerNumber = ({ currentTarget }: React.SyntheticEvent<HTMLInputElement>) => {
    const { value } = currentTarget;
    setFormSubmitionValues((state) => ({ ...state, customerNumber: value }));

    if (!isEmpty(value) && isOnlyNumbers(value)) {
      setValidationFeedback((state) => ({ ...state, customerNumber: '' }));
    } else {
      setValidationFeedback((state) => ({ ...state, customerNumber: formValidationFeedback.customerNumberInvalid }));
    }
  };

  const handlePin = ({ currentTarget }: React.SyntheticEvent<HTMLInputElement>) => {
    const { value } = currentTarget;
    setFormSubmitionValues((state) => ({ ...state, pin: value }));

    if (!isEmpty(value) && isOnlyNumbers(value) && isSpecificLength(value, 4)) {
      setValidationFeedback((state) => ({ ...state, pin: '' }));
    } else {
      setValidationFeedback((state) => ({ ...state, pin: formValidationFeedback.pinInvalid }));
    }
  };

  const handleFormSubmit = (event: React.SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();

    setTryAgainView(CreateUserResultTypes.USER_CREATE);

    if (!formStatus.valid) {
      setFormStatus((state) => ({ ...state, status: FormStatus.TRIED_SUBMITTING }));

      // focus first invalid input element.
      const invalidInput = (Object.keys(validationFeedback) as Array<keyof typeof validationFeedback>).filter((k) => validationFeedback[k] !== '');
      if (invalidInput.length > 0) {
        const input = document.getElementById(invalidInput[0]);
        if (input) {
          input.focus();
        }
      }

      return;
    }

    if (formSubmitionValues.customerNumber.length === 8) {
      // supporting customerNumber not starting with partnerNumber
      submitPendingUser({
        ...formSubmitionValues,
        customerNumber: formSubmitionValues.partnerNumber + formSubmitionValues.customerNumber,
      });
    } else {
      submitPendingUser(formSubmitionValues);
    }
    setFormStatus((state) => ({ ...state, status: FormStatus.SUBMITTING }));
  };

  const renderValidationFeeback = (validationFeedback: string) => {
    return formStatus.status === FormStatus.TRIED_SUBMITTING && !formStatus.valid ? (
      <div className="form__field__feedback__text">{validationFeedback}</div>
    ) : null;
  };

  const renderMigratedInputFields = () => {
    return (
      <>
        <div className="form__field">
          <label htmlFor="phone" className="form__field__label">
            {formfieldPhoneLabel}
          </label>
          <input
            onChange={handleMobileNumber}
            type="tel"
            id="phone"
            className="form__field__input"
            aria-describedby="phoneFeedback"
            aria-invalid={ariaInvalid(formStatus.status, validationFeedback.phone)}
            value={formSubmitionValues.mobileNumber}
          />
          <div className="form__field__feedback" id="phoneFeedback">
            {renderValidationFeeback(validationFeedback.phone)}
          </div>
        </div>
        <div className="form__field">
          <label htmlFor="email" className="form__field__label">
            {formfieldEmailLabel}
          </label>
          <input
            onChange={handleEmail}
            type="email"
            id="email"
            className="form__field__input"
            aria-describedby="emailFeedback"
            aria-invalid={ariaInvalid(formStatus.status, validationFeedback.email)}
            value={formSubmitionValues.email}
            autoComplete="email"
          />
          <div className="form__field__feedback" id="emailFeedback">
            {renderValidationFeeback(validationFeedback.email)}
          </div>
        </div>
      </>
    );
  };

  const renderLegacyInputFields = () => {
    return (
      <>
        <div className="form__field">
          <label htmlFor="customerNumber" className="form__field__label">
            {formfieldCustomerNumberLabel}
          </label>
          <input
            onChange={handleCustomerNumber}
            type="text"
            id="customerNumber"
            className="form__field__input"
            value={formSubmitionValues.customerNumber}
            pattern="[0-9]*"
            inputMode="numeric"
            aria-describedby="customerNumberFeedback"
            aria-invalid={ariaInvalid(formStatus.status, validationFeedback.customerNumber)}
          />
          <div className="form__field__feedback" id="customerNumberFeedback">
            {renderValidationFeeback(validationFeedback.customerNumber)}
          </div>
        </div>
        <div className="form__field">
          <label htmlFor="pin" className="form__field__label">
            {formfieldPinLabel}
          </label>
          <input
            onChange={handlePin}
            type="text"
            pattern="[0-9]*"
            inputMode="numeric"
            id="pin"
            className="form__field__input"
            aria-describedby="pinFeedback"
            aria-invalid={ariaInvalid(formStatus.status, validationFeedback.pin)}
            value={formSubmitionValues.pin}
          />
          <div className="form__field__feedback" id="pinFeedback">
            {renderValidationFeeback(validationFeedback.pin)}
          </div>
          <button className="btn-link forgot-pin" type="button" id="wp-forgot-pin" onClick={() => setView(CreateUserResultTypes.LEGACY_FORGOT_PIN)}>
            {legacyPinLink}
          </button>
        </div>
      </>
    );
  };

  const showValidationErrors = formStatus.status === FormStatus.TRIED_SUBMITTING && !formStatus.valid && validationFeedback.partner !== '';
  return (
    <>
      <CardHeader title={title} ingress={ingress} />
      <CardBody>
        {formStatus.valid && formStatus.status === FormStatus.SUBMITTING ? (
          <div className="form__loader" />
        ) : (
          <form
            onSubmit={handleFormSubmit}
            className={`form ${formStatus.valid && formStatus.status === FormStatus.SUBMITTING ? 'form__submitting' : ''}`}
            noValidate={true}>
            {referredPartnerNumber === '' && (
              <PartnerSelect
                partnerList={partnerList}
                partnerInfo={partnerInfo}
                handlePartnerChanged={handlePartnerChanged}
                showValidationError={showValidationErrors}
                textConfig={textConfig}
              />
            )}
            {partnerInfo.platform === Platforms.LEGACY ? renderLegacyInputFields() : renderMigratedInputFields()}
            {showValidationErrors && <ValidationSummary textConfig={textConfig} validationFeedback={validationFeedback} />}
            <div className="form__field">
              <div className="form__field__submit">
                <button type="submit" id="wp-submit-pending-user" className="form__field__submit__button">
                  {formSubmitButtonText}
                </button>
              </div>
            </div>
            {partnerInfo.platform === Platforms.LEGACY && <p className="form__legacy-help">{legacyHelp}</p>}
          </form>
        )}
      </CardBody>
    </>
  );
};
